import { router } from '@inertiajs/react'
import type { AdminTab } from '@/types/admin'

export interface AdminSection {
  key: AdminTab
  label: string
  /** One line under the section title: what lives here and what it affects. */
  description: string
}

export interface AdminSectionGroup {
  label: string
  sections: AdminSection[]
}

/**
 * The panel's map. Groups are the sidebar's eyebrows; the order within a group is the
 * order an admin most often needs them (the thing that changes daily first).
 */
export const ADMIN_SECTION_GROUPS: AdminSectionGroup[] = [
  {
    label: 'Panel',
    sections: [
      { key: 'overview', label: 'Overview', description: 'The whole platform at a glance.' },
    ],
  },
  {
    label: 'People',
    sections: [
      {
        key: 'users',
        label: 'Users',
        description: 'Every account: role, verification, two-factor and what they are learning.',
      },
      {
        key: 'invitation_codes',
        label: 'Invitation Codes',
        description: 'Sign-up is by invitation. Each code admits one new learner.',
      },
    ],
  },
  {
    label: 'Content',
    sections: [
      {
        key: 'languages',
        label: 'Languages',
        description: 'The languages a learner can enrol in, with how much each one holds.',
      },
      {
        key: 'readings',
        label: 'Readings',
        description: 'Every text any learner has added, newest first.',
      },
      {
        key: 'words',
        label: 'Words',
        description: 'The shared lexicon. Deleting a word removes it for everyone tracking it.',
      },
    ],
  },
  {
    label: 'Learning',
    sections: [
      {
        key: 'srs',
        label: 'Scheduling',
        description: 'The spaced-repetition curve every learner follows.',
      },
      {
        key: 'brain',
        label: 'Brain',
        description: 'How the vocabulary brain on each language panel is drawn.',
      },
      {
        key: 'source_badges',
        label: 'Badges',
        description:
          'A badge says where a meaning came from. It shows on the word panel, the word page, vocabulary and flashcards, never inside a text. A source with no badge shows nothing.',
      },
    ],
  },
  {
    label: 'Operations',
    sections: [
      {
        key: 'enrichment_runs',
        label: 'Enrichment Runs',
        description: 'Background lookups that fill in the words of a reading, across every text.',
      },
    ],
  },
]

export const ADMIN_SECTIONS: AdminSection[] = ADMIN_SECTION_GROUPS.flatMap((g) => g.sections)

export function findAdminSection(key: AdminTab): AdminSection {
  return ADMIN_SECTIONS.find((s) => s.key === key) ?? ADMIN_SECTIONS[0]
}

export function isAdminTab(value: string | null): value is AdminTab {
  return ADMIN_SECTIONS.some((s) => s.key === value)
}

/** A section's own address, the same one the panel writes to the URL. */
export function adminSectionHref(key: AdminTab): string {
  return key === 'overview' ? '/admin' : `/admin?section=${key}`
}

/** From a page below the panel, picking a section goes back to the panel itself. */
export function visitAdminSection(key: AdminTab) {
  router.visit(adminSectionHref(key))
}
